import { ClaimRow } from '../../../../models/api/view/claim-view/claim-row';
import { LeaveCalendar } from '../../../../models/api/view/claim/summary/leavecalendar';
import { getDateKey } from './leave-calendar-utils';

/**
 * Restricts employee-wide leave calendar rows to the ones belonging to the given
 * claim. The API returns every absence for the employee across all of their
 * leaves, so callers that report a single claim's dates must narrow it first.
 *
 * Matches on either identifier because the two back ends key rows differently:
 * LeaveLink returns the leave number (same value as recordId) while TAMS returns
 * the case number (fileNumber). Returns the list untouched when the claim
 * carries neither, so an unexpectedly sparse claim row is not silently blanked.
 */
export function filterAbsencesForClaim(
  absences: LeaveCalendar[],
  claim: Pick<ClaimRow, 'fileNumber' | 'recordId'>,
): LeaveCalendar[] {
  const keys = [claim?.fileNumber, claim?.recordId]
    .filter(Boolean)
    .map((key) => String(key).trim());

  if (!keys.length) {
    return absences ?? [];
  }

  return (absences ?? []).filter((absence) =>
    keys.includes(String(absence.absenceNumber ?? '').trim()),
  );
}

/**
 * Pending/Approved + event → keep policy.
 * Denied + event → show Event, hide Denied.
 * Otherwise keep only Approved/Pending/Denied.
 */
export function filterAbsencesForCalendar(
  rawAbsences: LeaveCalendar[],
): LeaveCalendar[] {
  if (!rawAbsences?.length) {
    return [];
  }

  const groups = new Map<string, LeaveCalendar[]>();
  for (const absence of rawAbsences) {
    const key = `${getDateKey(new Date(absence.date))}|${absence.absenceNumber ?? ''}`;
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key)!.push(absence);
  }

  const result: LeaveCalendar[] = [];
  for (const group of groups.values()) {
    const events = group.filter((a) =>
      ['impactingevent', 'nonimpactingevent'].includes(a.status?.toLowerCase()),
    );
    const policies = group.filter((a) =>
      ['approved', 'pending', 'denied'].includes(a.status?.toLowerCase()),
    );
    const hasPendingOrApproved = policies.some((p) =>
      ['pending', 'approved'].includes(p.status?.toLowerCase()),
    );
    const hasDenied = policies.some((p) => p.status?.toLowerCase() === 'denied');

    if (events.length && hasPendingOrApproved) {
      result.push(...policies);
    } else if (events.length && hasDenied) {
      result.push(
        ...policies.filter((p) => p.status?.toLowerCase() !== 'denied'),
        ...events.map((e) => ({
          ...e,
          type: e.status.replace(/([a-z])([A-Z])/g, '$1 $2'),
          status: 'Event',
        })),
      );
    } else {
      result.push(...policies);
    }
  }

  return result;
}
